import { useMemo } from 'react';
import type { BlockResponse } from './types';
import { useExpertReportsDetails } from './useExpertReportsDetails';

/**
 * Чем читается результат: справочником своих значений, баллом с размерностью — или, у совмещённой
 * шкалы, и тем, и другим сразу.
 */
export type ExpertReportResultShape = {
  /** Возможные баллы; `null` — баллами результат не считается */
  possiblePoints: number[] | null;
  /** Справочник, которым оценивается результат; `null` — справочника у результата нет */
  possibleValues: string[] | null;
  /** Размерность балла; `null` — размерность не определена */
  unit: string | null;
};

/** Вид результатов одного заключения: итога и каждого его блока */
export type ExpertReportResultShapes = {
  total: ExpertReportResultShape;
  blocks: Map<number, ExpertReportResultShape>;
};

/** Соответствие `reportId → вид результатов заключения` */
export type ExpertReportResultShapesMap = Map<number, ExpertReportResultShapes>;

/** Вид результата, когда о заключении ещё ничего не известно */
export const UNKNOWN_RESULT_SHAPE: ExpertReportResultShape = {
  possiblePoints: null,
  possibleValues: null,
  unit: null,
};

const collectBlockShapes = (blocks: BlockResponse[], acc: Map<number, ExpertReportResultShape>) => {
  for (const block of blocks) {
    acc.set(block.id, {
      possiblePoints: block.possiblePoints,
      possibleValues: block.possibleValues,
      unit: block.unit,
    });
    if (block.children?.length) collectBlockShapes(block.children, acc);
  }
  return acc;
};

/**
 * Вид результатов указанных экспертных заключений: баллы, справочник значений и размерность у итога
 * и у каждого блока. Читается из карточек заключений ({@link useExpertReportsDetails}).
 *
 * Нужен там, где вид результата определяет саму подачу колонки (отбор выбором из справочника
 * против диапазона баллов), — знать его надо до того, как придут сами результаты. Дерево блоков
 * (`useExpertReportsBlocksMap`) этого не несёт, поэтому берётся карточка заключения; запрашиваются
 * только те заключения, чьи колонки выведены.
 */
export const useExpertReportsResultShapes = (reportIds: number[]): ExpertReportResultShapesMap => {
  const { details } = useExpertReportsDetails(reportIds);

  return useMemo(() => {
    const map: ExpertReportResultShapesMap = new Map();
    details.forEach((report, reportId) => {
      map.set(reportId, {
        // Итог складывается правилом расчёта заключения: возможных баллов у него нет —
        // есть справочник, из которого правило выбирает итог-значение
        total: { possiblePoints: null, possibleValues: report.possibleValues, unit: report.unit },
        blocks: collectBlockShapes(report.blocks, new Map()),
      });
    });
    return map;
  }, [details]);
};
